import { useTranslation } from "react-i18next";
import {
  Copy,
  ExternalLink,
  Pencil,
  UserCheck,
  UserX,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nexus/ui";
import type { UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { RowActionsTrigger } from "../RowActionsTrigger";

interface MemberActionsMenuProps {
  user: UserItem;
  onEditProfile?: (user: UserItem) => void;
  onOpenAuditLog?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
}

/** Per-member row actions dropdown, shared by the Org and Role drawers' member lists. */
export function MemberActionsMenu({ user, onEditProfile, onOpenAuditLog, onSuspend, onUnsuspend }: MemberActionsMenuProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <RowActionsTrigger variant="hover-group-member" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuItem className="gap-2" onClick={() => onEditProfile?.(user)}>
              <Pencil className="h-3.5 w-3.5" />
              {t("iam.personDrawer.actions.editProfile")}
            </DropdownMenuItem>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[220px]">
            {t("iam.personDrawer.actionTooltips.editProfile")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuItem
              className="gap-2"
              onClick={() => navigator.clipboard.writeText(user.email)}
            >
              <Copy className="h-3.5 w-3.5" />
              {t("iam.personDrawer.actions.copyEmail")}
            </DropdownMenuItem>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[220px]">
            {t("iam.personDrawer.actionTooltips.copyEmail")}
          </TooltipContent>
        </Tooltip>
        {onOpenAuditLog && (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem className="gap-2" onClick={() => onOpenAuditLog(user)}>
                <ExternalLink className="h-3.5 w-3.5" />
                {t("iam.personDrawer.actions.viewAuditLog")}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              {t("iam.personDrawer.actionTooltips.viewAuditLog")}
            </TooltipContent>
          </Tooltip>
        )}
        <DropdownMenuSeparator />
        {user.status === "DEACTIVATED" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem className="gap-2" onClick={() => onUnsuspend?.(user)}>
                <UserCheck className="h-3.5 w-3.5" />
                {t("iam.personDrawer.actions.unsuspend")}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              {t("iam.personDrawer.actionTooltips.unsuspend")}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => onSuspend?.(user)}
              >
                <UserX className="h-3.5 w-3.5" />
                {t("iam.personDrawer.actions.suspend")}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              {t("iam.personDrawer.actionTooltips.suspend")}
            </TooltipContent>
          </Tooltip>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
